// Fechas de StorSafe en la hora de Ecuador (America/Guayaquil, UTC-5 todo el año).
//
// Dos clases de valores llegan de la base:
//  - Fechas sin hora ('YYYY-MM-DD', columnas DATE): son un día de calendario y se muestran TAL CUAL, sin convertir.
//    Ojo: new Date('2026-10-31') las toma como medianoche UTC y, en Ecuador, toLocaleDateString muestra el día
//    anterior (30/10/2026); por eso aFechaLocal las arma como medianoche local.
//  - Instantes (timestamptz: approved_at, signed_at, created_at...): llegan en UTC. Cortar el texto
//    ('2026-11-02T03:52:00+00:00'.slice(0, 10)) da el día UTC, que entre las 19:00 y la medianoche de Ecuador es el
//    día siguiente. Se pasan al día de Ecuador con diaEc / fechaEc, sin depender de la zona del equipo.
// Las fechas por defecto de los formularios y los nombres de archivo usan hoyEc().

const SOLO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;

export const ZONA_ECUADOR = 'America/Guayaquil';
/** Desfase fijo de Ecuador continental (sin horario de verano desde 1993). */
const DESFASE_ECUADOR = '-05:00';

const partesEcuador = new Intl.DateTimeFormat('en-US', { timeZone: ZONA_ECUADOR, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Día (AAAA-MM-DD) en Ecuador de un instante; '' si no es una fecha válida. */
export function diaDeInstanteEc(instante: Date | number | string): string {
  const d = instante instanceof Date ? instante : new Date(instante);
  if (Number.isNaN(d.getTime())) return '';
  const p: Record<string, string> = {};
  for (const x of partesEcuador.formatToParts(d)) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

/** Hoy en Ecuador (AAAA-MM-DD): fechas por defecto de los formularios y nombres de archivo. */
export const hoyEc = (ahora: Date = new Date()): string => diaDeInstanteEc(ahora);

/**
 * Día (AAAA-MM-DD) de un valor de la base: una fecha sin hora (DATE) se deja tal cual; un instante (timestamptz) se
 * pasa al día de Ecuador. '' sin valor.
 */
export function diaEc(valor: string | null | undefined): string {
  if (!valor) return '';
  if (SOLO_FECHA.test(valor)) return valor;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? valor.slice(0, 10) : diaDeInstanteEc(d);
}

/** dd/mm/aaaa del día de Ecuador (una fecha sin hora, tal cual); '—' sin valor. */
export function fechaEc(valor: string | null | undefined): string {
  const m = SOLO_FECHA.exec(diaEc(valor));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/** Instante de la medianoche de Ecuador de un día, para guardar un día en una columna timestamptz (próximo cobro). */
export const inicioDiaEc = (dia: string): string => `${dia}T00:00:00${DESFASE_ECUADOR}`;
/** Último segundo del día en Ecuador (vencimientos que valen hasta el final del día). */
export const finDiaEc = (dia: string): string => `${dia}T23:59:59${DESFASE_ECUADOR}`;

// ── Aritmética de calendario sobre AAAA-MM-DD (sin horas ni zonas) ──
const aUtc = (dia: string) => { const m = SOLO_FECHA.exec(dia); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : NaN; };
const deUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Suma días a un día de calendario. */
export const sumarDiasDia = (dia: string, dias: number): string => deUtc(aUtc(dia) + dias * 86_400_000);

/** Suma meses a un día de calendario; el 31 en un mes sin 31 queda en el último día del mes destino. */
export function sumarMesesDia(dia: string, meses: number): string {
  const m = SOLO_FECHA.exec(dia);
  if (!m) return dia;
  const anio = +m[1], mes = +m[2] - 1 + meses, d = +m[3];
  const ultimo = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
  return deUtc(Date.UTC(anio, mes, Math.min(d, ultimo)));
}

/** Date para mostrar: 'YYYY-MM-DD' como medianoche local; lo demás, como new Date(valor). */
export function aFechaLocal(valor: string): Date {
  const m = SOLO_FECHA.exec(valor);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(valor);
}

/**
 * Fecha con el formato del locale (dd/mm/aaaa por defecto, o '—' sin valor): una fecha sin hora, tal cual; un
 * instante, en el día de Ecuador.
 */
export function fechaCortaLocal(valor: string | null | undefined, locale = 'es-EC',
  opciones: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }): string {
  if (!valor) return '—';
  const d = aFechaLocal(valor);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, SOLO_FECHA.test(valor) ? opciones : { ...opciones, timeZone: ZONA_ECUADOR });
}
